import type {FogRenderRequest, FogRenderResponse, FogScene} from "./SoftFogWorkerProtocol.ts";

type Job = {request: FogRenderRequest; scene: FogScene};

export class SoftFogWorkerClient {
    private generation = 0;
    private validFromGeneration = 0;
    private active: Job | undefined;
    private pending: Job | undefined;
    private disposed = false;

    constructor(
        private readonly worker: Worker,
        private readonly onFrame: (scene: FogScene, bitmap: ImageBitmap, tintBitmap: ImageBitmap) => void,
        private readonly onFailure: () => void,
    ) {
        worker.onmessage = event => this.receive(event.data as FogRenderResponse);
        worker.onerror = () => this.fail();
    }

    request(scene: FogScene): void {
        if (this.disposed) return;
        const request = {id: ++this.generation, ...scene, timeSeconds: Date.now() / 1000};
        this.pending = {request, scene};
        this.dispatch();
    }

    invalidate(): void {
        this.generation++;
        this.validFromGeneration = this.generation;
        this.pending = undefined;
    }

    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.pending = undefined;
        this.active = undefined;
        this.worker.terminate();
    }

    private dispatch(): void {
        if (this.disposed || this.active || !this.pending) return;
        this.active = this.pending;
        this.pending = undefined;
        try {
            this.worker.postMessage(this.active.request);
        } catch {
            this.fail();
        }
    }

    private receive(response: FogRenderResponse): void {
        if (this.disposed || response.id !== this.active?.request.id) {
            response.bitmap?.close();
            response.tintBitmap?.close();
            return;
        }
        const completed = this.active;
        this.active = undefined;
        if (response.error || !response.bitmap || !response.tintBitmap) {
            response.bitmap?.close();
            response.tintBitmap?.close();
            this.fail();
            return;
        }
        // A completed intermediate frame is still fresher than the displayed one.
        // Publishing it avoids waiting until token movement stops entirely.
        if (response.id >= this.validFromGeneration && response.id <= this.generation) {
            this.onFrame(completed.scene, response.bitmap, response.tintBitmap);
        } else {
            response.bitmap.close();
            response.tintBitmap.close();
        }
        this.dispatch();
    }

    private fail(): void {
        if (this.disposed) return;
        this.dispose();
        this.onFailure();
    }
}
